import { ApolloError } from "@apollo/client";
import { Alert, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { SelectElement, SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlClientType } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

export type ClientFormInput = {
    name: string;
    type: GqlClientType;
    address: string;
    contactName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    website?: string | null;
    profilePicture?: string | null;
    isAllMapEditorLayersDefaultVisible: boolean;
};

type Props = {
    initialData: ClientFormInput;
    onSubmit: SubmitHandler<ClientFormInput>;
    formButtonLabel: string;
    error?: ApolloError;
};

export default function ClientForm({ initialData, onSubmit, error, formButtonLabel }: Props) {
    const { t } = useTranslation("client");

    const {
        handleSubmit,
        register,
        setError,
        control,
        formState: { errors, isSubmitting, isLoading, isDirty, isSubmitted },
    } = useForm<ClientFormInput>({
        values: initialData,
    });
    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    label={t("clientForm.name")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    {...register("name")}
                />

                <SelectElement
                    control={control}
                    label={t("clientForm.type")}
                    name="type"
                    valueKey="value"
                    rules={{ required: true }}
                    options={Object.values(GqlClientType).map(value => ({
                        label: t(`clientForm.typeValue.${value}`),
                        value,
                    }))}
                />

                <TextField
                    label={t("clientForm.address")}
                    error={!!errors.address}
                    helperText={errors.address?.message}
                    {...register("address")}
                />

                <SwitchElement
                    name="isAllMapEditorLayersDefaultVisible"
                    label={t("clientForm.allLayersVisible")}
                    control={control}
                />

                <Divider>
                    <Typography variant="caption" textTransform="uppercase">
                        {t("clientForm.sectionContact")}
                    </Typography>
                </Divider>

                <TextField
                    label={t("clientForm.contactName")}
                    error={!!errors.contactName}
                    helperText={errors.contactName?.message}
                    {...register("contactName")}
                />

                <TextField
                    label={t("clientForm.contactEmail")}
                    error={!!errors.contactEmail}
                    helperText={errors.contactEmail?.message}
                    {...register("contactEmail")}
                />

                <TextField
                    label={t("clientForm.contactPhone")}
                    error={!!errors.contactPhone}
                    helperText={errors.contactPhone?.message}
                    {...register("contactPhone")}
                />

                <Button sx={{ mt: 3 }} type="submit" color="primary" variant="contained" disabled={!isDirty} loading={isLoading}>
                    {formButtonLabel}
                </Button>
            </Stack>
        </form>
    );
}
